/**
 * GDPR/TKG §165-compliant cookie consent for Google Analytics (GA4).
 *
 * GA4 must NOT load (and must not set _ga/_gid cookies) until the visitor
 * explicitly accepts analytics cookies. The choice is persisted in
 * localStorage so it survives reloads, and can be revoked at any time via
 * the "Cookie settings" affordance in <CookieConsentBanner />.
 */

const CONSENT_STORAGE_KEY = "dmy_cookie_consent";

/**
 * Fired to ask <CookieConsentBanner /> to re-open itself, e.g. from the
 * footer's "Cookie settings" link. The banner owns its own open/closed
 * state locally, so a DOM event is the simplest way to reach it from
 * anywhere in the tree without adding a Redux slice just for this.
 */
export const COOKIE_CONSENT_REOPEN_EVENT = "dmy:cookie-consent:reopen";

export function requestCookieConsentReopen(): void {
  window.dispatchEvent(new Event(COOKIE_CONSENT_REOPEN_EVENT));
}

export type CookieConsentChoice = "accepted" | "rejected";

let gaScriptLoaded = false;

/** Reads the stored consent choice. Returns null if no decision was made yet. */
export function getStoredConsent(): CookieConsentChoice | null {
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === "accepted" || value === "rejected" ? value : null;
  } catch {
    // localStorage unavailable (private browsing, disabled storage, etc.)
    return null;
  }
}

export function setStoredConsent(choice: CookieConsentChoice): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Non-critical — the banner will just reappear on next visit
  }
}

/** Clears the stored choice so the consent banner is shown again. */
export function clearStoredConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function getGaId(): string | undefined {
  return import.meta.env.VITE_GA_ID;
}

/** Deletes any GA cookies (_ga, _gid, _ga_<container-id>) already set for this domain. */
function clearGaCookies(): void {
  const names = document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0])
    .filter((name) => name === "_ga" || name === "_gid" || name.startsWith("_ga_"));

  for (const name of names) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
  }
}

/**
 * Loads gtag.js and initializes GA4. Safe to call multiple times — only
 * injects the script once. No-op if VITE_GA_ID is not configured.
 */
export function loadGoogleAnalytics(): void {
  const gaId = getGaId();
  if (!gaId || gaScriptLoaded) return;
  gaScriptLoaded = true;

  // Clear any prior opt-out flag set by disableGoogleAnalytics()
  (window as unknown as Record<string, boolean>)[`ga-disable-${gaId}`] = false;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  gtag("js", new Date());
  gtag("config", gaId);
}

/**
 * Disables GA tracking via gtag.js's official opt-out flag (works even if
 * the script was already loaded during a previous "accepted" session) and
 * clears any GA cookies already set.
 */
export function disableGoogleAnalytics(): void {
  const gaId = getGaId();
  if (gaId) {
    (window as unknown as Record<string, boolean>)[`ga-disable-${gaId}`] = true;
  }
  clearGaCookies();
}

/**
 * Call once at app boot. Loads (or keeps disabled) GA4 based on a
 * previously stored choice. Does nothing when no choice has been made yet —
 * <CookieConsentBanner /> is responsible for asking and reacting.
 */
export function initAnalyticsFromStoredConsent(): void {
  const consent = getStoredConsent();
  if (consent === "accepted") {
    loadGoogleAnalytics();
  } else if (consent === "rejected") {
    disableGoogleAnalytics();
  }
}
